import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import JSZip from 'jszip';
import { TranslateModule } from '@ngx-translate/core';
import { DocumentTypeService } from '../common/service/document-type.service';
import { DocumentType } from '../model';
import { TranslationService } from '../common';
import { formatBytes } from './document-manager.utils';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';
import { PDF_WORKER_SRC } from './document-manager.utils';

GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

export interface ZipExplorerResult {
  title: string;
  data: Uint8Array;
  mimeType: string;
  extension: string;
  size: number;
  documentTypeId?: string | null;
  documentTypeName?: string | null;
  documentDate?: Date | null;
}

interface ZipEntryView {
  id: string;
  path: string;
  name: string;
  extension: string;
  mimeType: string;
  size: number;
  selected: boolean;
  title: string;
  documentTypeId: string;
  documentTypeName: string;
  documentDate: Date;
  previewSrc?: string | null;
}

interface ZipTreeNode {
  id: string;
  name: string;
  path: string;
  isDirectory: boolean;
  size: number;
  children: ZipTreeNode[];
  entry?: ZipEntryView;
}

type DocumentCategory = DocumentType['category'];
type CategoryGroup = { category: DocumentCategory; labelKey: string; types: DocumentType[] };

const SUPPORTED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

@Component({
  selector: 'app-zip-explorer-dialog',
  standalone: true,
  templateUrl: './zip-explorer-dialog.component.html',
  styleUrls: ['./zip-explorer-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    FormsModule,
    TranslateModule,
  ],
})
export class ZipExplorerDialogComponent implements OnInit {
  /** Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  protected readonly data = inject<{ file: File; mode?: 'local' | 'link'; documentTypeCategory?: 'company' | 'chain' | 'report' | 'attributes' | null; queuePosition?: number; queueTotal?: number }>(MAT_DIALOG_DATA);
  protected readonly queuePosition = this.data.queuePosition;
  protected readonly queueTotal = this.data.queueTotal;
  readonly dialogRef = inject(MatDialogRef<ZipExplorerDialogComponent, ZipExplorerResult[]>);
  private readonly dialog = inject(MatDialog);
  private readonly entryMap = new Map<string, JSZip.JSZipObject>();
  protected readonly entries = signal<ZipEntryView[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly tree = signal<ZipTreeNode[]>([]);
  private readonly expandedState = signal<Record<string, boolean>>({});
  private readonly translation = inject(TranslationService);
  private readonly documentTypeService = inject(DocumentTypeService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly documentTypes = signal<DocumentType[]>([]);
  protected readonly typesLoading = signal(true);
  protected readonly isLinkMode = this.data.mode === 'link';
  private readonly typeSearchTerm = signal('');
  protected readonly typeSearchControl = this.fb.control('');
  protected readonly typeSearchActive = computed(() => !!this.typeSearchTerm());
  protected readonly typeGroups = computed(() => this.buildCategoryGroups(this.filterTypes(this.documentTypes(), this.typeSearchTerm())));

  constructor() {
    this.typeSearchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.typeSearchTerm.set((value ?? '').toLowerCase().trim());
    });
  }

  async ngOnInit() {
    try {
      await this.loadTypes();
      const zip = await JSZip.loadAsync(this.data.file);
      const nextEntries: ZipEntryView[] = [];
      Object.values(zip.files).forEach((zipEntry, index) => {
        if (zipEntry.dir) {
          return;
        }
        const extension = this.extensionFromName(zipEntry.name);
        if (!SUPPORTED_EXTENSIONS.includes(extension)) {
          return;
        }
        const name = zipEntry.name.split('/').pop() ?? zipEntry.name;
        if (name.startsWith('.')) {
          return;
        }
        const title = this.titleFromName(name);
        const rawEntry: any = zipEntry;
        const size = rawEntry?._data?.uncompressedSize ?? 0;
        this.entryMap.set(zipEntry.name, zipEntry);
        nextEntries.push({
          id: `${index}-${zipEntry.name}`,
          path: zipEntry.name,
          name,
          extension,
          mimeType: this.mimeTypeFromExtension(extension),
          size,
          selected: true,
          title,
          documentTypeId: '',
          documentTypeName: '',
          documentDate: new Date(),
          previewSrc: null,
        });
      });
      this.entries.set(nextEntries);
      if (!nextEntries.length) {
        this.error.set(await this.translation.loadFromBackend('No_supported_files_found_inside_the_archive_tr'));
      }
      await this.loadEntryPreviews(nextEntries);
      this.refreshTree(true);
    } catch (error) {
      console.error('Failed to read zip file', error);
      this.error.set(await this.translation.loadFromBackend('Unable_to_open_the_ZIP_file_tr'));
    } finally {
      this.loading.set(false);
    }
  }

  protected hasSelection() {
    return this.entries().some((entry) => entry.selected);
  }

  protected toggleAll(selected: boolean) {
    this.entries.update((current) => current.map((entry) => ({ ...entry, selected })));
    this.refreshTree(false);
  }

  protected async importSelected() {
    const selectedEntries = this.entries().filter((entry) => entry.selected);
    if (!selectedEntries.length || !this.canImportSelected()) {
      return;
    }
    this.loading.set(true);
    try {
      const results: ZipExplorerResult[] = [];
      for (const entry of selectedEntries) {
        const fileEntry = this.entryMap.get(entry.path);
        if (!fileEntry) {
          continue;
        }
        const data = await fileEntry.async('uint8array');
        results.push({
          title: entry.title?.trim() || entry.name,
          data,
          mimeType: entry.mimeType,
          extension: entry.extension,
          size: data.byteLength,
          documentTypeId: entry.documentTypeId || null,
          documentTypeName: entry.documentTypeName || null,
          documentDate: entry.documentDate ?? null,
        });
      }
      this.dialogRef.close(results);
    } catch (error) {
      console.error('Failed to extract zip entries', error);
      this.error.set(await this.translation.loadFromBackend('Failed_to_extract_the_selected_entries_tr'));
    } finally {
      this.loading.set(false);
    }
  }

  protected formatBytes(bytes: number) {
    return formatBytes(bytes);
  }

  protected canImportSelected() {
    if (!this.isLinkMode) {
      return this.entries().some((entry) => entry.selected);
    }
    return this.entries()
      .filter((entry) => entry.selected)
      .every((entry) => !!entry.documentTypeId);
  }

  protected onDocumentTypeChange(entry: ZipEntryView, typeId: string) {
    entry.documentTypeId = typeId;
    entry.documentTypeName = this.documentTypes().find((type) => type.id === typeId)?.name ?? '';
  }

  protected filterTypes(types: DocumentType[], term: string) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) {
      return types;
    }
    return types.filter((type) => type.name.toLowerCase().includes(normalized));
  }

  private buildCategoryGroups(types: DocumentType[]): CategoryGroup[] {
    const grouped = new Map<DocumentCategory, DocumentType[]>();
    types.forEach((type) => {
      const category = (type.category ?? 'company') as DocumentCategory;
      const bucket = grouped.get(category);
      if (bucket) {
        bucket.push(type);
      } else {
        grouped.set(category, [type]);
      }
    });
    return Array.from(grouped.entries())
      .map(([category, list]) => ({
        category,
        labelKey: this.categoryLabelKey(category),
        types: [...list].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  private categoryLabelKey(category: DocumentCategory) {
    switch (category) {
      case 'chain':
        return 'chain_tr';
      case 'report':
        return 'Reports_tr';
      case 'attributes':
        return 'product_attributes_tr';
      default:
        return 'Company_tr';
    }
  }

  protected documentTypeLabel(typeId: string) {
    return this.documentTypes().find((type) => type.id === typeId)?.name ?? '';
  }

  protected previewSrc(node: ZipTreeNode) {
    return node.entry?.previewSrc ?? null;
  }

  protected async previewEntry(node: ZipTreeNode) {
    if (node.isDirectory || !node.entry) {
      return;
    }
    try {
      const fileEntry = this.entryMap.get(node.entry.path);
      if (!fileEntry) return;
      const data = await fileEntry.async('uint8array');
      const blob = new Blob([data.slice()], { type: node.entry.mimeType });
      const tempDocument = {
        id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
        title: node.entry.title || node.entry.name,
        mimeType: node.entry.mimeType,
        extension: node.entry.extension,
        kind: node.entry.extension === 'pdf' ? 'pdf' : 'image',
        blob,
        size: data.byteLength,
        createdAt: new Date().toISOString(),
        pages: 1,
        source: 'zip-entry' as const,
      };
      const { DocumentWorkbenchDialogComponent } = await import('./document-workbench-dialog.component');
      const pdfDataUrl =
        node.entry.extension === 'pdf'
          ? await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            })
          : undefined;
      this.dialog.open(DocumentWorkbenchDialogComponent, {
        data: {
          mode: node.entry.extension === 'pdf' ? 'split' : 'preview',
          document: tempDocument,
          pdfDataUrl,
          maxPages: 1,
          allowSaveUpload: false,
          hideSplitActions: true,
          actionLabels: 'local',
          documentTypeCategory: this.data.documentTypeCategory ?? null,
          readOnlyPreview: true,
        },
        width: '100vw',
        maxWidth: '100vw',
        height: '100dvh',
        maxHeight: '100dvh',
        panelClass: 'document-workbench-dialog',
      });
    } catch (error) {
      console.error('Failed to preview ZIP entry', error);
    }
  }

  protected trackNode(_: number, node: ZipTreeNode) {
    return node.id;
  }

  protected isExpanded(path: string) {
    return this.expandedState()[path] ?? false;
  }

  protected toggleDirectory(path: string) {
    this.expandedState.update((current) => ({ ...current, [path]: !current[path] }));
  }

  private refreshTree(resetExpansion: boolean) {
    const nodes = this.buildTree(this.entries());
    this.tree.set(nodes);
    const expanded = resetExpansion ? {} : { ...this.expandedState() };
    this.ensureExpansionDefaults(nodes, expanded);
    this.expandedState.set(expanded);
  }

  private async loadEntryPreviews(entries: ZipEntryView[]) {
    for (const entry of entries) {
      const fileEntry = this.entryMap.get(entry.path);
      if (!fileEntry) {
        continue;
      }
      try {
        const data = await fileEntry.async('uint8array');
        if (entry.extension === 'pdf') {
          entry.previewSrc = await this.renderPdfPreview(data);
        } else {
          entry.previewSrc = await this.renderImagePreview(data, entry.mimeType);
        }
      } catch (error) {
        console.warn('Failed to build ZIP entry preview', error);
      }
    }
    this.entries.set([...entries]);
  }

  private async renderImagePreview(data: Uint8Array, mimeType: string) {
    return await new Promise<string>((resolve, reject) => {
      const blob = new Blob([data.slice()], { type: mimeType });
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private async renderPdfPreview(data: Uint8Array) {
    const pdf = await getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 0.22 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvas: canvas, canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/png');
  }

  private async loadTypes() {
    this.typesLoading.set(true);
    try {
      this.documentTypes.set(await this.documentTypeService.listAll(this.data.documentTypeCategory ?? undefined));
    } catch (error) {
      console.error('Failed to load document types for ZIP explorer', error);
      this.documentTypes.set([]);
    } finally {
      this.typesLoading.set(false);
    }
  }

  private buildTree(entries: ZipEntryView[]) {
    const dirMap = new Map<string, ZipTreeNode>();
    const roots: ZipTreeNode[] = [];

    const ensureDir = (segments: string[]) => {
      let currentPath = '';
      let parent: ZipTreeNode | undefined;
      segments.forEach((segment) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        let node = dirMap.get(currentPath);
        if (!node) {
          node = {
            id: `dir-${currentPath}`,
            name: segment,
            path: currentPath,
            isDirectory: true,
            size: 0,
            children: [],
          };
          dirMap.set(currentPath, node);
          if (parent) {
            parent.children.push(node);
          } else {
            roots.push(node);
          }
        }
        parent = node;
      });
      return parent;
    };

    entries.forEach((entry) => {
      const segments = entry.path.split('/');
      const fileName = segments.pop() ?? entry.name;
      const parent = segments.length ? ensureDir(segments) : undefined;
      const node: ZipTreeNode = {
        id: entry.id,
        name: fileName,
        path: entry.path,
        isDirectory: false,
        size: entry.size,
        children: [],
        entry,
      };
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
      let aggregatePath = '';
      segments.forEach((segment) => {
        aggregatePath = aggregatePath ? `${aggregatePath}/${segment}` : segment;
        const dir = dirMap.get(aggregatePath);
        if (dir) {
          dir.size += entry.size;
        }
      });
    });

    const sortNodes = (nodes: ZipTreeNode[]) => {
      nodes.sort((a, b) => {
        if (a.isDirectory !== b.isDirectory) {
          return a.isDirectory ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
      nodes.forEach((node) => {
        if (node.children.length) {
          sortNodes(node.children);
        }
      });
    };

    sortNodes(roots);
    return roots;
  }

  private ensureExpansionDefaults(nodes: ZipTreeNode[], expanded: Record<string, boolean>, level = 0) {
    nodes.forEach((node) => {
      if (node.isDirectory) {
        if (!(node.path in expanded)) {
          expanded[node.path] = level === 0;
        }
        if (node.children.length) {
          this.ensureExpansionDefaults(node.children, expanded, level + 1);
        }
      }
    });
  }

  private extensionFromName(name: string) {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private titleFromName(name: string) {
    return (
      name
        .replace(/\.[^/.]+$/, '')
        .replace(/[_-]+/g, ' ')
        .trim() || name
    );
  }

  private mimeTypeFromExtension(extension: string) {
    if (extension === 'pdf') {
      return 'application/pdf';
    }
    return `image/${extension === 'jpg' ? 'jpeg' : extension}`;
  }
}
